import { useTranslations } from "next-intl";
import { AcceptanceState } from "@/types/enums/AcceptanceState";

/**
 * Token classes per acceptance state. Modeled on TCCGuestStatus's map for the
 * sibling field (`state`), so the two status columns read as one system.
 *
 * SYSTEM gets its OWN colour, and that is the point of this component. The
 * requests action cell used to be binary — `ACCEPTED` or "everything else" —
 * so a waitlisted guest was painted with the red decline chip, indistinguishable
 * from a rejection, and PENDING looked declined too.
 */
const STATE_CLASS: Record<string, string> = {
  ACCEPTED: "user-icon-font-4 user-icon-bg-4 outline-user-icon-4",
  DECLINED: "user-icon-font-3 user-icon-bg-3 outline-user-icon-3",
  PENDING: "user-icon-font-1 user-icon-bg-1 outline-user-icon-1",
  SYSTEM: "user-icon-font-12 user-icon-bg-12 outline-user-icon-12",
};

type AcceptanceStateChipProps = {
  /** Raw stored value. Anything unrecognised renders as "status unknown". */
  state?: string | null;
  testId?: string;
};

/**
 * Read-only acceptance status.
 *
 * A `<span>`, deliberately — the archive cell used to render this as a real
 * `<button>` with `cursor-default`, no `onClick` and no `disabled`. That put a
 * dead control in the tab order AND swallowed the row click, because the row's
 * open-detail handler skips anything inside `button, a, input, [role=button]`.
 * So the status cell was the one cell in the table that could not open the guest.
 */
export const AcceptanceStateChip: React.FC<AcceptanceStateChipProps> = ({
  state,
  testId,
}) => {
  const tState = useTranslations("optionsMultiSingleSelect");
  const tRequests = useTranslations("eventGuestsRequests");

  const known =
    state != null && Object.values(AcceptanceState).includes(state as AcceptanceState);
  const label = known ? tState(state as string) : tRequests("stateUnknown");
  const classes = known
    ? STATE_CLASS[state as string]
    : "user-icon-font-1 user-icon-bg-1 outline-user-icon-1";

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 body-4-regular w-max ${classes}`}
      data-testid={testId}
      data-state={known ? state : "UNKNOWN"}
    >
      {label}
    </span>
  );
};
